<div class="container-fluid py-3">
  <!-- Processing Loader Overlay -->
  <div
    *ngIf="isProcessingImages"
    class="processing-overlay position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
    style="background: rgba(0,0,0,0.85); z-index: 2000;">
    <div class="text-center">
      <div class="spinner-border text-warning mb-3" role="status" style="width: 4rem; height: 4rem;">
        <span class="visually-hidden">Loading...</span>
      </div>
      <h4 class="text-white mb-2">Processing Images...</h4>
      <p class="text-warning fs-5">
        {{ processingProgress.current }} / {{ processingProgress.total }}
      </p>
      <div class="progress" style="width: 300px; height: 8px;">
        <div
          class="progress-bar bg-warning"
          role="progressbar"
          [style.width.%]="(processingProgress.current / processingProgress.total) * 100"
          [attr.aria-valuenow]="processingProgress.current"
          [attr.aria-valuemin]="0"
          [attr.aria-valuemax]="processingProgress.total">
        </div>
      </div>
      <p class="text-muted mt-3">Drawing bounding boxes on scanned frames</p>
    </div>
  </div>

  <div class="d-flex justify-content-between align-items-center mb-4">
    <h4 class="text-white mb-0" id="resultTitle">
      Scanned Results
    </h4>

    <div class="sort-container">
      <label for="sortSelect" class="sort-label">
        <i class="bi bi-funnel sort-icon"></i>
        <span class="nowrap">Sort by:</span>
      </label>

      <select
        id="sortSelect"
        class="form-control sort mb-0 w-100"
        (change)="updateSort($event)">
        <option value="confidence">Confidence Level</option>
        <option value="faces">Number of Faces</option>
        <option value="time">Time</option>
      </select>
    </div>
  </div>

  <div class="row">
    <!-- Left Side: Faces Grid -->
    <div class="col-lg-7">
      <div class="row">
        <div
          class="col-lg-4 col-sm-4 col-md-3 mb-4"
          *ngFor="let face of extractedFaceImages; let i = index">
          <div
            class="face-card text-center p-2 bg-dark rounded shadow-sm cursor-pointer"
            (click)="selectFrame(i)"
            style="position: relative;">

            <!--
              <input
                type="checkbox"
                [(ngModel)]="face.selected"
                style="position: absolute; top: 15px; right: 8px; accent-color: #22c063; width: 18px; height: 18px;"
              />
            -->

            <div class="face-card-image-wrap">
              <img
                [src]="face.imageWithBoxes || face.image"
                [alt]="'Face at ' + formatTime(face.timestamp)"
                class="w-100"
                style="max-width: 250px; max-height: 180px; object-fit: contain; border-radius: 4px;" />

              <button
                type="button"
                class="result-lens-btn"
                aria-label="Search this image"
                data-tooltip="Search this image"
                (click)="searchWithFace(face.image, $event)">
                <svg
                  class="result-lens-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M8 4h8a4 4 0 0 1 4 4v5.5M16 20H8a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4"
                  />
                  <circle cx="12" cy="12" r="3.2" fill="currentColor" />
                  <circle cx="16.5" cy="16.5" r="1.4" fill="currentColor" />
                </svg>
              </button>
            </div>

            <div class="d-flex justify-content-center align-items-center">
              <p class="text-light mt-2 mb-0">
                <strong><i class="bi bi-clock"></i></strong>
                {{ formatTime(face.timestamp) || 'N/A' }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Side: Selected Face Details -->
    <div class="col-lg-5">
      <div class="card right-card w-100">
        <div class="card-body d-flex flex-column align-items-center">
          <!-- Zoom Controls -->
          <div
            class="zoom-controls mb-3 d-flex justify-content-center align-items-center w-100 gap-2">
            <label class="zoom-label text-warning fw-semibold mb-0">
              Zoom
            </label>

            <input
              type="range"
              min="0.5"
              max="10"
              step="0.1"
              [(ngModel)]="zoomLevel"
              (input)="onZoomChange()"
              class="form-range zoom-slider w-50" />

            <span class="zoom-value text-warning fw-bold">
              {{ zoomLevel.toFixed(1) }}x
            </span>
          </div>

          <!-- Selected Face Canvas -->
          <div class="croppedFaceContainer">
            <canvas #selectedFaceCanvas></canvas>

            <div *ngIf="isMirroring" class="mirror-loading">
              <div class="spinner-border text-warning" role="status"></div>
            </div>

            <!-- Top-right icon -->
            <button
              class="icon-btn top-right-icon cursor-pointer"
              title="More options"
              (click)="toggleOptions()">
              <i class="bi bi-three-dots-vertical"></i>
            </button>

            <!-- Tooltip Menu -->
            <div class="tooltip-icons" [class.show]="showOptions">
              <!-- Face enhance temporarily disabled
              <button class="icon-btn" title="Enhance" (click)="openEnhance()">
                <i class="bi bi-magic cursor-pointer"></i>
              </button>
              -->

              <button
                class="icon-btn"
                title="Mirror Image"
                (click)="mirrorImage()">
                <i class="bi bi-arrow-left-right cursor-pointer"></i>
              </button>
            </div>
          </div>

          <!-- Face Details -->
          <div *ngIf="selectedFace" class="w-100 mb-3 my-3">
            <p class="mb-1">
              <span class="text-warning">Confidence:</span>
              {{
                (selectedFace.data.detected_faces[0].confidence * 100)
                  | number: '1.2-2'
              }}%
            </p>

            <p class="mb-1">
              <span class="text-warning">Size:</span>
              {{ selectedFace.data.detected_faces[0].bbox.width }} ×
              {{ selectedFace.data.detected_faces[0].bbox.height }}
            </p>

            <p class="mb-1">
              <span class="text-warning">Position:</span>
              X: {{ selectedFace.data.detected_faces[0].bbox.x }}, Y:
              {{ selectedFace.data.detected_faces[0].bbox.y }}
            </p>
          </div>

          <!-- Extract Button -->
          <button
            *ngIf="selectedFace"
            class="btn btn-warning mt-auto"
            style="background: linear-gradient(180deg, #32ea87 0%, #22c063 100%); color: #042212; border: none;"
            (click)="extractSingleFace()">
            Extract Faces
          </button>

          <!-- Cropped Faces -->
          <div
            *ngIf="showFaces"
            class="d-flex justify-content-start align-items-center flex-wrap gap-2 mt-3">
            <div
              *ngFor="let face of croppedFaces; let i = index"
              class="croppedFaceContainer position-relative">
              <img [src]="face" class="croppedFace" />

              <button
                type="button"
                class="result-lens-btn result-lens-btn--left"
                aria-label="Search this image"
                data-tooltip="Search this image"
                (click)="searchWithFace(face, $event)">
                <svg
                  class="result-lens-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M8 4h8a4 4 0 0 1 4 4v5.5M16 20H8a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4"
                  />
                  <circle cx="12" cy="12" r="3.2" fill="currentColor" />
                  <circle cx="16.5" cy="16.5" r="1.4" fill="currentColor" />
                </svg>
              </button>

              <div class="tooltip-icons">
                <a
                  [href]="face"
                  [download]="'face-' + i + '.jpg'"
                  class="icon-btn"
                  title="Download">
                  <i class="bi bi-download"></i>
                </a>
              </div>
            </div>
          </div>

          <!-- Placeholder -->
          <div *ngIf="!selectedFace" class="text-center text-muted mt-5">
            No face selected
            <br />
            Select a face from the left to see details here.
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Face enhance temporarily disabled
<div
  *ngIf="enhance.show"
  class="enhance-overlay position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
  style="background: rgba(0,0,0,0.7); z-index: 1050;">
  <div
    class="enhance-card bg-dark text-white p-4 rounded shadow-lg"
    style="width: 400px; max-width: 90%;">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0 text-warning">Enhance Face</h5>
      <button
        type="button"
        class="btn-close btn-close-white"
        aria-label="Close"
        (click)="closeEnhance()">
      </button>
    </div>

    <div class="text-center mb-3">
      <img
        [src]="enhance.image"
        class="img-fluid rounded border border-secondary"
        alt="Enhancing Face"
        style="max-height: 220px;" />
    </div>

    <div class="d-flex justify-content-end gap-2">
      <button
        *ngIf="enhance.showReset"
        class="btn btn-secondary"
        (click)="resetEnhance()">
        Reset
      </button>

      <button
        *ngIf="!enhance.started"
        class="btn btn-warning text-dark fw-semibold"
        (click)="onEnhanceClick()">
        Enhance
      </button>

      <button
        *ngIf="enhance.started && !enhance.completed"
        disabled
        class="btn btn-warning text-dark fw-semibold"
        (click)="onEnhanceClick()">
        <span class="spinner-border spinner-border-sm" aria-hidden="true">
        </span>
        Enhancing ..
      </button>

      <button
        *ngIf="enhance.started && enhance.completed"
        class="btn btn-warning text-dark fw-semibold"
        (click)="applyEnhanceResult()">
        Apply
      </button>
    </div>
  </div>
</div>
-->
